/**
 * The board's tools: its own (select, hand, frame, eraser, arrow) and those the node kinds offered
 * in the environment bring (`NodeKindDef.board.tool`: Art's note, text, shape, image and pen;
 * UI/UX's elements; a community plugin's), in the tool bar's order, each with its words, key
 * and picture. The board names no kind: a kind's tool draws an item of that kind.
 */
import { createMemo, createSignal } from "solid-js";
import type { Keybind } from "@protocol/Keybind";
import type { BoardCtx } from "./context";
import type { BoardPart, BoardTool, OwnTool, ToolInfo } from "./types";
import { LATE_ORDER } from "./constants";

/** The board's own tools, with their words, keys and where they stand on the tool bar (the kinds' tools stand by their own `order`). */
const OWN: Record<OwnTool, { label: string; key: string; order: number }> = {
  select: { label: "Select", key: "V", order: -30 },
  hand: { label: "Hand (pan)", key: "H", order: -20 },
  frame: { label: "Frame", key: "Shift+F", order: -10 },
  eraser: { label: "Eraser (rubs out drawn lines)", key: "E", order: 1000 },
  arrow: { label: "Arrow", key: "A", order: 1010 },
};

/** Every tool the board itself has, by id, for the tool bar's pictures and the config's names. */
export const OWN_TOOLS = Object.keys(OWN) as OwnTool[];

/**
 * The tools of the kinds offered here: each kind's own tool, by `order` (then the kinds' order);
 * a second kind with the same tool id is left out.
 */
export function kindTools(offered: readonly { id: string; label: string }[], look: BoardCtx["deps"]["kinds"]["board"]): (ToolInfo & { order: number })[] {
  const seen = new Set<string>(OWN_TOOLS);
  const tools: (ToolInfo & { order: number })[] = [];
  for (const kind of offered) {
    const board = look(kind.id);
    const tool = board.tool;
    if (!tool || seen.has(tool.id)) continue;
    seen.add(tool.id);
    tools.push({ id: tool.id, label: tool.label || kind.label, key: tool.key ?? "", icon: tool.icon || board.element, kind: kind.id, element: board.element, order: tool.order ?? LATE_ORDER });
  }
  return tools.sort((a, b) => a.order - b.order);
}

/** The kind a tool draws here: the kind bringing the tool, else (a tool the board has, as `frame`) none. */
export function kindFor(ctx: BoardCtx, t: BoardTool): string | null {
  return ctx.board.tools().find((tool) => tool.id === t)?.kind ?? null;
}

/** The first kind offered here that is drawn by hand (the pen's, else any), if one is. */
export function drawingKind(ctx: BoardCtx): string | null {
  const { kinds } = ctx.deps;
  const chosen = ctx.board.tools().find((t) => t.id === ctx.board.tool());
  if (chosen?.element === "drawing" && chosen.kind) return chosen.kind;
  return kinds.nodes().find((k) => kinds.board(k.id).element === "drawing")?.id ?? null;
}

/** The tools' members of the board service. */
export function toolsPart(ctx: BoardCtx): BoardPart<"tool" | "setTool" | "tools" | "toolBinds" | "toolFor" | "toolInfo"> {
  const { kinds, frames } = ctx.deps;
  const [tool, setTool] = createSignal<BoardTool>("select");
  const own = (id: OwnTool, label = OWN[id].label): ToolInfo & { order: number } => ({ id, label, key: OWN[id].key, icon: id, order: OWN[id].order });
  const tools = createMemo<ToolInfo[]>(() => {
    const drawn = kindTools(kinds.nodes(), kinds.board);
    const all = [own("select"), own("hand"), own("frame", frames()?.tool ?? OWN.frame.label), ...drawn];
    if (drawn.some((t) => t.element === "drawing")) all.push(own("eraser"));
    if (kinds.links().length > 0) all.push(own("arrow"));
    return all.sort((a, b) => a.order - b.order).map(({ order: _, ...t }) => t);
  });
  const toolBinds = createMemo<Keybind[]>(() => tools().flatMap((t) => (t.key ? [{ keys: t.key, action: "board.tool", argument: t.id }] : [])));
  const toolInfo = (id: BoardTool) => tools().find((t) => t.id === id) ?? null;
  return { tool, setTool, tools, toolBinds, toolInfo, toolFor: (element) => tools().find((t) => t.element === element)?.id ?? element };
}
